import * as THREE from "three";
import { useEffect, useRef, useState, memo, forwardRef, useImperativeHandle } from "react";
import { Line2 } from "three/examples/jsm/lines/Line2";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";

const DimensionLineComponent = forwardRef(({
  params,
  line_width,
  start,
  end,
  visible,
  lineStyle = "solid",
  scaleFactor = 1,
  diameter = false,
  lineOffset = 5,
  scaledArrowHeight,
  minArrowHeight = 2.5
}, ref) => {
  const lineRef = useRef();
  const line1Ref = useRef(null); // Ref to first Line2 object
  const line2Ref = useRef(null); // Ref to second Line2 object
  const geometry1Ref = useRef(null); // Ref to first LineGeometry
  const geometry2Ref = useRef(null); // Ref to second LineGeometry
  const materialRef = useRef(null); // Ref to LineMaterial
  const externalLineArrowHeightMultiple = 2;

  // Reusable Vector3 objects for calculations (created once, reused every update)
  const tempVec1Ref = useRef(new THREE.Vector3());
  const tempVec2Ref = useRef(new THREE.Vector3());
  const tempVec3Ref = useRef(new THREE.Vector3());
  const tempVec4Ref = useRef(new THREE.Vector3());
  const tempVec5Ref = useRef(new THREE.Vector3());
  const tempVec6Ref = useRef(new THREE.Vector3());

  // Expose updatePosition method for direct updates via ref
  useImperativeHandle(ref, () => ({
    updatePosition: (newStart, newEnd) => {
      if (!line1Ref.current || !line2Ref.current || !geometry1Ref.current || !geometry2Ref.current || !materialRef.current) return;

      // Reuse temporary vectors instead of creating new ones
      const tempVec1 = tempVec1Ref.current;
      const tempVec2 = tempVec2Ref.current;
      const tempVec3 = tempVec3Ref.current;
      const tempVec4 = tempVec4Ref.current;
      const tempVec5 = tempVec5Ref.current;
      const tempVec6 = tempVec6Ref.current;

      // Calculate direction and offset points using reusable vectors
      tempVec1.subVectors(newEnd, newStart).normalize(); // direction
      const lineOffsetReworked = lineStyle === "dotted" || diameter ? 0 : (lineOffset / scaleFactor + (scaledArrowHeight || 0));
      
      tempVec2.subVectors(newEnd, newStart);
      const distance = tempVec2.length();   

      const isInvertedArrows = (scaledArrowHeight !== undefined && (scaledArrowHeight > 0.25 * distance) && lineStyle !== "dotted");

      
      const lineOffSetTotal = THREE.MathUtils.clamp(lineOffsetReworked, lineStyle === "dotted" || diameter ? 
        0 : minArrowHeight / scaleFactor, distance * 0.2);
      
      // Calculate offset points using reusable vectors
      // Note: We reuse tempVec3-6 but need to be careful about order
      let offsetStart, offsetEnd, offsetStart2, offsetEnd2;
      if (!isInvertedArrows) {
        // Calculate offsetStart and offsetEnd first
        tempVec4.copy(tempVec1).multiplyScalar(lineOffSetTotal);
        offsetStart = tempVec3.copy(newStart).add(tempVec4);
        offsetEnd = tempVec5.copy(newEnd).sub(tempVec4);
        // Calculate offsetStart2 and offsetEnd2 (reuse tempVec4)
        offsetStart2 = tempVec3.copy(newStart).add(tempVec4);
        offsetEnd2 = tempVec5.copy(newEnd).sub(tempVec4);
      } else {
        // Calculate offsetStart and offsetEnd first
        tempVec4.copy(tempVec1).multiplyScalar(lineOffSetTotal);
        offsetStart = tempVec3.copy(newStart).sub(tempVec4);
        tempVec6.copy(tempVec1).multiplyScalar(lineOffSetTotal + externalLineArrowHeightMultiple * scaledArrowHeight);
        offsetEnd = tempVec5.copy(newStart).sub(tempVec6);
        // Calculate offsetStart2 and offsetEnd2
        offsetStart2 = tempVec3.copy(newEnd).add(tempVec4);
        offsetEnd2 = tempVec5.copy(newEnd).add(tempVec6);
      }

      const points = [offsetStart.x, offsetStart.y, offsetStart.z, offsetEnd.x, offsetEnd.y, offsetEnd.z];
      const points2 = [offsetStart2.x, offsetStart2.y, offsetStart2.z, offsetEnd2.x, offsetEnd2.y, offsetEnd2.z];

      // Update existing geometries
      geometry1Ref.current.setPositions(points);
      line1Ref.current.computeLineDistances();
      
      geometry2Ref.current.setPositions(points2);
      line2Ref.current.computeLineDistances();
    },
    getLineRef: () => lineRef.current
  }), [lineStyle, scaleFactor, lineOffset, scaledArrowHeight, minArrowHeight, diameter, externalLineArrowHeightMultiple]);

  // Initialize geometry, material, and line objects only once
  useEffect(() => {
    if (!lineRef.current) return;

    // Only create if they don't exist
    if (!materialRef.current) {
      materialRef.current = new LineMaterial({
        color: params.line_colour,
        opacity: 1,
        linewidth: line_width,
        resolution: new THREE.Vector2(params.canvas_width, params.canvas_height),
        transparent: false,
        lights: false,
      });
    }

    if (!geometry1Ref.current) {
      geometry1Ref.current = new LineGeometry();
    }

    if (!geometry2Ref.current) {
      geometry2Ref.current = new LineGeometry();
    }

    if (!line1Ref.current) {
      line1Ref.current = new Line2(geometry1Ref.current, materialRef.current);
      lineRef.current.add(line1Ref.current);
    }

    if (!line2Ref.current) {
      line2Ref.current = new Line2(geometry2Ref.current, materialRef.current);
      lineRef.current.add(line2Ref.current);
    }

    // Cleanup on unmount
    return () => {
      if (geometry1Ref.current) {
        geometry1Ref.current.dispose();
        geometry1Ref.current = null;
      }
      if (geometry2Ref.current) {
        geometry2Ref.current.dispose();
        geometry2Ref.current = null;
      }
      if (materialRef.current) {
        materialRef.current.dispose();
        materialRef.current = null;
      }
      if (lineRef.current && line1Ref.current) {
        lineRef.current.remove(line1Ref.current);
        line1Ref.current = null;
      }
      if (lineRef.current && line2Ref.current) {
        lineRef.current.remove(line2Ref.current);
        line2Ref.current = null;
      }
    };
  }, []); // Only run once on mount

  // Update line positions when start/end or other props change
  useEffect(() => {
    if (!line1Ref.current || !line2Ref.current || !geometry1Ref.current || !geometry2Ref.current || !materialRef.current) return;

    // Calculate direction and offset points
    const direction = new THREE.Vector3().subVectors(end, start).normalize();
    const lineOffsetReworked = lineStyle === "dotted" || diameter ? 0 : (lineOffset / scaleFactor + (scaledArrowHeight || 0));
    const distance = new THREE.Vector3().subVectors(end, start).length();   

    const isInvertedArrows = (scaledArrowHeight !== undefined && (scaledArrowHeight > 0.25 * distance) && lineStyle !== "dotted");
    
    const lineOffSetTotal = THREE.MathUtils.clamp(lineOffsetReworked, lineStyle === "dotted" || diameter ? 
      0 : minArrowHeight / scaleFactor, distance * 0.2);
    
    const offsetStart = !isInvertedArrows ? new THREE.Vector3().copy(start).add(direction.clone().multiplyScalar(lineOffSetTotal)) : 
      new THREE.Vector3().copy(start).sub(direction.clone().multiplyScalar(lineOffSetTotal));
    const offsetEnd = !isInvertedArrows ? new THREE.Vector3().copy(end).sub(direction.clone().multiplyScalar(lineOffSetTotal)) : 
      new THREE.Vector3().copy(start).sub(direction.clone().multiplyScalar(lineOffSetTotal + externalLineArrowHeightMultiple * scaledArrowHeight));
    
    const offsetStart2 = !isInvertedArrows ? new THREE.Vector3().copy(start).add(direction.clone().multiplyScalar(lineOffSetTotal)) : 
      new THREE.Vector3().copy(end).add(direction.clone().multiplyScalar(lineOffSetTotal));
    const offsetEnd2 = !isInvertedArrows ? new THREE.Vector3().copy(end).sub(direction.clone().multiplyScalar(lineOffSetTotal)) : 
      new THREE.Vector3().copy(end).add(direction.clone().multiplyScalar(lineOffSetTotal + externalLineArrowHeightMultiple * scaledArrowHeight));

    const points = [offsetStart.x, offsetStart.y, offsetStart.z, offsetEnd.x, offsetEnd.y, offsetEnd.z];
    const points2 = [offsetStart2.x, offsetStart2.y, offsetStart2.z, offsetEnd2.x, offsetEnd2.y, offsetEnd2.z];

    // Update existing geometries instead of creating new ones
    geometry1Ref.current.setPositions(points);
    line1Ref.current.computeLineDistances();
    
    geometry2Ref.current.setPositions(points2);
    line2Ref.current.computeLineDistances();

    // Update material properties if needed
    if (materialRef.current.linewidth !== line_width) {
      materialRef.current.linewidth = line_width;
      materialRef.current.needsUpdate = true;
    }
    if (materialRef.current.color.getHex() !== new THREE.Color(params.line_colour).getHex()) {
      materialRef.current.color.set(params.line_colour);
    }

    // Configure dashed line if necessary
    if (lineStyle === "dotted") {
      materialRef.current.dashed = true;
      materialRef.current.dashSize = 1 / scaleFactor;
      materialRef.current.gapSize = 0.5 / scaleFactor;
      materialRef.current.defines.USE_DASH = "";
      materialRef.current.needsUpdate = true;
    } else {
      materialRef.current.dashed = false;
      delete materialRef.current.defines.USE_DASH;
      materialRef.current.needsUpdate = true;
    }

    // Update resolution if canvas size changed
    if (materialRef.current.resolution.x !== params.canvas_width || materialRef.current.resolution.y !== params.canvas_height) {
      materialRef.current.resolution.set(params.canvas_width, params.canvas_height);
      materialRef.current.needsUpdate = true;
    }
  }, [start, end, lineStyle, scaleFactor, params.line_colour, line_width, scaledArrowHeight, lineOffset, minArrowHeight, diameter, params.canvas_width, params.canvas_height]);
  
  return <group ref={lineRef} visible={visible} />;
});

DimensionLineComponent.displayName = 'DimensionLine';

// Memoize to prevent unnecessary re-renders
export const DimensionLine = memo(DimensionLineComponent, (prevProps, nextProps) => {
  // Compare vector values instead of references
  const startEqual = prevProps.start?.x === nextProps.start?.x && 
                     prevProps.start?.y === nextProps.start?.y && 
                     prevProps.start?.z === nextProps.start?.z;
  const endEqual = prevProps.end?.x === nextProps.end?.x && 
                   prevProps.end?.y === nextProps.end?.y && 
                   prevProps.end?.z === nextProps.end?.z;
  
  // Only re-render if these props actually change
  return (
    startEqual &&
    endEqual &&
    prevProps.visible === nextProps.visible &&
    prevProps.lineStyle === nextProps.lineStyle &&
    prevProps.scaleFactor === nextProps.scaleFactor &&
    prevProps.line_width === nextProps.line_width &&
    prevProps.scaledArrowHeight === nextProps.scaledArrowHeight &&
    prevProps.params.line_colour === nextProps.params.line_colour &&
    prevProps.lineOffset === nextProps.lineOffset &&
    prevProps.params.canvas_width === nextProps.params.canvas_width &&
    prevProps.params.canvas_height === nextProps.params.canvas_height
  );
});